import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Plus,
  Download,
  ChevronDown,
  Search,
  MoreVertical,
  FileText,
  Trash2,
  Archive,
  RotateCcw,
  Pencil,
} from "lucide-react";
import { logger } from "../services/logService";
import sharedStepService from "../services/sharedStepService";
import ErrorBoundary from "../components/ErrorBoundary";
import toast from "react-hot-toast";
import PageSkeleton from "../components/skeletons/PageSkeleton";

// Utility function to format relative dates
const formatRelativeDate = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now - date;
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return "Today";
  if (diffInDays === 1) return "1 day ago";
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 14) return "1 week ago";
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  if (diffInDays < 60) return "1 month ago";
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
  return `${Math.floor(diffInDays / 365)} years ago`;
};

// Utility to strip HTML tags for display
const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const SharedStepsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectId = searchParams.get("project_id");
  const [sharedSteps, setSharedSteps] = useState([]);
  const [filteredSteps, setFilteredSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [sortBy, setSortBy] = useState("most_recent");
  const [assignTo, setAssignTo] = useState("all");
  const [selectedSteps, setSelectedSteps] = useState(new Set());

  // Dropdown states
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);

  // Refs for dropdown
  const assignDropdownRef = useRef(null);
  const sortDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const menuRefs = useRef({});

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        assignDropdownRef.current &&
        !assignDropdownRef.current.contains(event.target)
      ) {
        setShowAssignDropdown(false);
      }
      if (
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(event.target)
      ) {
        setShowSortDropdown(false);
      }
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setShowStatusDropdown(false);
      }

      // Close menu dropdowns
      if (openMenuId !== null) {
        const menuRef = menuRefs.current[openMenuId];
        if (menuRef && !menuRef.contains(event.target)) {
          setOpenMenuId(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenuId]);

  // Fetch shared steps
  useEffect(() => {
    const fetchSharedSteps = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await sharedStepService.getSharedSteps(projectId, {
          search: searchQuery,
        });

        const steps =
          response?.data?.shared_steps || response?.shared_steps || [];
        setSharedSteps(steps);
      } catch (error) {
        const errorMessage = "Failed to fetch shared steps";
        setError(errorMessage);
        logger.error(error, errorMessage);
        toast.error(errorMessage);
        setSharedSteps([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSharedSteps();
  }, [projectId, refreshTrigger, searchQuery]);

  // Filter and sort shared steps
  useEffect(() => {
    let filtered = [...sharedSteps];

    // Apply status filter
    if (selectedStatus !== "all") {
      filtered = filtered.filter((step) => {
        const status = step.status || "active";
        return status === selectedStatus;
      });
    }

    // Apply assign to filter
    if (assignTo === "me") {
      // Filter by current user - you'll need to get the current user ID
      // filtered = filtered.filter(step => step.created_by === currentUserId);
    }

    // Apply sorting
    if (sortBy === "most_recent") {
      filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (sortBy === "oldest") {
      filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }

    setFilteredSteps(filtered);
  }, [sharedSteps, selectedStatus, sortBy, assignTo]);

  const handleCreateSharedStep = () => {
    navigate(`/shared-steps/create?project_id=${projectId}`);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedSteps(
        new Set(filteredSteps.map((step) => step.shared_step_id)),
      );
    } else {
      setSelectedSteps(new Set());
    }
  };

  const handleSelectStep = (stepId) => {
    const newSelected = new Set(selectedSteps);
    if (newSelected.has(stepId)) {
      newSelected.delete(stepId);
    } else {
      newSelected.add(stepId);
    }
    setSelectedSteps(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedSteps.size === 0) return;

    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedSteps.size} shared step(s)?`,
      )
    ) {
      return;
    }

    try {
      // Delete all selected steps
      await Promise.all(
        Array.from(selectedSteps).map((stepId) =>
          sharedStepService.deleteSharedStep(stepId),
        ),
      );
      toast.success(
        `${selectedSteps.size} shared step(s) deleted successfully!`,
      );
      setSelectedSteps(new Set());
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      logger.error(error, "Failed to delete shared steps");
      toast.error("Failed to delete some shared steps");
    }
  };

  const handleBulkArchive = () => {
    toast("Archive functionality coming soon");
  };

  const handleBulkRestore = () => {
    toast("Restore functionality coming soon");
  };

  const handleViewSharedStep = (step) => {
    navigate(
      `/shared-steps/view/${step.shared_step_id}?project_id=${projectId}`,
    );
  };

  const handleEditSharedStep = (step) => {
    navigate(
      `/shared-steps/edit/${step.shared_step_id}?project_id=${projectId}`,
    );
  };

  const handleDeleteSharedStep = async (step) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${stripHtml(step.step_description)}"?`,
      )
    ) {
      return;
    }

    try {
      await sharedStepService.deleteSharedStep(step.shared_step_id);
      toast.success("Shared step deleted successfully!");
      setRefreshTrigger((prev) => prev + 1);
      setOpenMenuId(null);
    } catch (error) {
      logger.error(error, "Failed to delete shared step");
      toast.error(error.message || "Failed to delete shared step");
    }
  };

  // Get status badge color and text
  const getStatusBadge = (step) => {
    const status = step.status || "active";

    if (status === "active") {
      return { text: "Active", className: "bg-red-50 text-red-600" };
    } else if (status === "draft") {
      return { text: "Draft", className: "bg-teal-50 text-teal-600" };
    } else if (status === "archived") {
      return { text: "Archived", className: "bg-gray-100 text-gray-600" };
    }
    return { text: "Active", className: "bg-red-50 text-red-600" };
  };

  // Get test case count (placeholder - you may need to add this to your API)
  const getTestCaseCount = (step) => {
    return step.test_case_count || Math.floor(Math.random() * 30000);
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4">
        <PageSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600">
        {error}
      </div>
    );
  }

  const hasSelection = selectedSteps.size > 0;

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-2 py-3">
          <h1 className="text-2xl font-bold text-gray-900">Shared Steps</h1>
        </div>

        <div className="px-8 py-6">
          {/* Main Heading Section */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">
                Manage Shared Steps
              </h2>
              <p className="text-sm text-gray-500">
                Organize and manage group of test cases.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleCreateSharedStep}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 flex items-center gap-2 text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Shared Step
              </button>
              <div className="relative">
                <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 flex items-center gap-2 text-sm font-medium">
                  <Download className="w-4 h-4" />
                  Import Shared Step
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Filter and Search Bar */}
          <div className="flex items-center gap-3 mb-4">
            {/* Selection Mode */}
            {hasSelection ? (
              <>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded-md">
                  <input
                    type="checkbox"
                    checked={true}
                    onChange={() => setSelectedSteps(new Set())}
                    className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">All</span>
                </div>

                <div className="flex items-center px-3 py-1.5 bg-red-50 border border-red-200 rounded-md">
                  <span className="text-sm text-red-700 font-medium">
                    {selectedSteps.size} Selected
                  </span>
                </div>

                <button
                  onClick={handleBulkDelete}
                  className="px-4 py-1.5 text-sm text-white bg-teal-500 border border-teal-500 rounded-md hover:bg-teal-600 flex items-center gap-2 font-medium transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>

                <button
                  onClick={handleBulkArchive}
                  className="px-4 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2 font-medium transition-colors"
                >
                  <Archive className="w-4 h-4" />
                  Archive
                </button>

                <button
                  onClick={handleBulkRestore}
                  className="px-4 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2 font-medium transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Restore
                </button>
              </>
            ) : (
              <>
                <label className="flex items-center gap-2 cursor-pointer px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={
                      selectedSteps.size === filteredSteps.length &&
                      filteredSteps.length > 0
                    }
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                  />
                  <span>All</span>
                </label>

                {/* Assign To Dropdown */}
                <div className="relative" ref={assignDropdownRef}>
                  <button
                    onClick={() => setShowAssignDropdown(!showAssignDropdown)}
                    className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
                  >
                    Assign To: {assignTo === "all" ? "Me" : "All"}
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  {showAssignDropdown && (
                    <div className="absolute top-full left-0 mt-1 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                      <button
                        onClick={() => {
                          setAssignTo("me");
                          setShowAssignDropdown(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Me
                      </button>
                      <button
                        onClick={() => {
                          setAssignTo("all");
                          setShowAssignDropdown(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                      >
                        All
                      </button>
                    </div>
                  )}
                </div>

                {/* Sort Dropdown */}
                <div className="relative" ref={sortDropdownRef}>
                  <button
                    onClick={() => setShowSortDropdown(!showSortDropdown)}
                    className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
                  >
                    Sort: {sortBy === "most_recent" ? "Most Recent" : "Oldest"}
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  {showSortDropdown && (
                    <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                      <button
                        onClick={() => {
                          setSortBy("most_recent");
                          setShowSortDropdown(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Most Recent
                      </button>
                      <button
                        onClick={() => {
                          setSortBy("oldest");
                          setShowSortDropdown(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Oldest
                      </button>
                    </div>
                  )}
                </div>

                {/* Status Dropdown */}
                <div className="relative" ref={statusDropdownRef}>
                  <button
                    onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                    className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
                  >
                    Status
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  {showStatusDropdown && (
                    <div className="absolute top-full left-0 mt-1 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                      <button
                        onClick={() => {
                          setSelectedStatus("all");
                          setShowStatusDropdown(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                      >
                        All
                      </button>
                      <button
                        onClick={() => {
                          setSelectedStatus("active");
                          setShowStatusDropdown(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Active
                      </button>
                      <button
                        onClick={() => {
                          setSelectedStatus("draft");
                          setShowStatusDropdown(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Draft
                      </button>
                      <button
                        onClick={() => {
                          setSelectedStatus("archived");
                          setShowStatusDropdown(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Archived
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Search */}
            <div className="flex-1 max-w-md ml-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search test suites..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-1.5 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
          </div>

          {/* Shared Steps List */}
          {filteredSteps.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4 bg-white rounded-lg border border-gray-200">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-6">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                No Shared Steps Available
              </h3>
              <p className="text-sm text-gray-600 text-center mb-8 max-w-md">
                Create a shared step by clicking below and save time by reusing
                it across multiple test cases & runs.
              </p>
              <button
                onClick={handleCreateSharedStep}
                className="px-6 py-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-medium"
              >
                Create Shared Step
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredSteps.map((step) => {
                const statusBadge = getStatusBadge(step);
                // const testCaseCount = getTestCaseCount(step);
                const title = stripHtml(step.step_description || "Untitled");
                const description = stripHtml(
                  step.notes || step.expected_result || "",
                );
                const isSelected = selectedSteps.has(step.shared_step_id);

                return (
                  <div
                    key={step.shared_step_id}
                    className={`flex items-center gap-4 p-4 border rounded-lg transition-all cursor-pointer ${
                      isSelected
                        ? "bg-red-50 border-red-200 shadow-sm"
                        : "bg-white border-gray-200 hover:shadow-sm"
                    }`}
                    onClick={() => !isSelected && handleViewSharedStep(step)}
                  >
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSelectStep(step.shared_step_id);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500 cursor-pointer"
                    />

                    {/* Document Icon */}
                    <div
                      className={`w-10 h-10 flex items-center justify-center rounded ${
                        isSelected ? "bg-red-100" : "bg-gray-50"
                      }`}
                    >
                      <FileText
                        className={`w-5 h-5 ${isSelected ? "text-red-600" : "text-gray-500"}`}
                      />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3
                        className={`font-semibold mb-0.5 ${isSelected ? "text-gray-900" : "text-gray-900"}`}
                      >
                        {title}
                      </h3>
                      <p
                        className={`text-sm line-clamp-1 ${isSelected ? "text-gray-600" : "text-gray-500"}`}
                      >
                        {description ||
                          "Shared step for regression testing of main features."}
                      </p>
                    </div>

                    {/* Right Side Info */}
                    <div className="flex items-center gap-6">
                      <span className="text-sm text-gray-500 whitespace-nowrap min-w-[100px] text-right">
                        {formatRelativeDate(step.created_at)}
                      </span>

                      <span
                        className={`px-3 py-1 rounded-md text-xs font-medium ${statusBadge.className} min-w-[70px] text-center`}
                      >
                        {statusBadge.text}
                      </span>

                      {/* <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewSharedStep(step);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700 hover:underline whitespace-nowrap min-w-[100px] text-right"
                      >
                        {testCaseCount} Test Cases
                      </button> */}

                      {/* Three Dots Menu */}
                      <div
                        className="relative"
                        ref={(el) =>
                          (menuRefs.current[step.shared_step_id] = el)
                        }
                      >
                        <button
                          className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(
                              openMenuId === step.shared_step_id
                                ? null
                                : step.shared_step_id,
                            );
                          }}
                        >
                          <MoreVertical className="w-5 h-5" />
                        </button>

                        {/* Dropdown Menu */}
                        {openMenuId === step.shared_step_id && (
                          <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-20">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditSharedStep(step);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                            >
                              <Pencil className="w-4 h-4" />
                              Edit
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSharedStep(step);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default SharedStepsPage;
